/**
 * Утилита для обработки согласования слов по роду в шаблонах
 * Поддерживает синтаксис: {вариант1|вариант2} и {вариант1|вариант2:variable_name}
 * Также допускает третий вариант для среднего рода: {муж|жен|сред:variable_name}
 */

import { detectGenderFromVariable } from './genderDetection';

/**
 * Regex для поиска конструкций согласования
 * Формат: {вариант1|вариант2} или {вариант1|вариант2:variable_name} или {вариант1|вариант2|вариант3:variable_name}
 */
// Второй вариант не должен захватывать суффикс после ":", поэтому исключаем двоеточие
const GENDER_AGREEMENT_REGEX = /\{([^|{}]+)\|([^|{}:]+)(?:\|([^|{}:]+))?(?::([^}]+))?\}/g;

/**
 * Regex для поиска переменных в тексте
 * Формат: {{variable_name}}
 */
const VARIABLE_REGEX = /\{\{([^}]+)\}\}/g;

/**
 * Находит ближайшую переменную слева от указанной позиции
 * @param {string} text - Текст для поиска
 * @param {number} position - Позиция, от которой ищем
 * @returns {string|null} - Имя найденной переменной или null
 */
function findNearestVariable(text, position) {
    // Получаем текст слева от позиции
    const textBefore = text.substring(0, position);

    // Ищем все переменные в тексте слева
    const matches = [];
    let match;
    const regex = new RegExp(VARIABLE_REGEX);

    while ((match = regex.exec(textBefore)) !== null) {
        matches.push({
            name: match[1].trim(),
            position: match.index
        });
    }

    // Возвращаем самую близкую (последнюю найденную)
    if (matches.length > 0) {
        return matches[matches.length - 1].name;
    }

    return null;
}

/**
 * Парсит текст и находит все конструкции согласования
 * @param {string} text - Текст для парсинга
 * @returns {Array} - Массив найденных конструкций
 */
export function parseGenderAgreement(text) {
    if (!text || typeof text !== 'string') {
        return [];
    }

    const agreements = [];
    let match;
    const regex = new RegExp(GENDER_AGREEMENT_REGEX);

    while ((match = regex.exec(text)) !== null) {
        // Важно: НЕ обрабатываем конструкции внутри {{...}} (это переменные шаблона).
        // Например: {{debtor_case_act_type|lowerFirst}} содержит подстроку {debtor_case_act_type|lowerFirst},
        // но это не согласование по роду.
        const startIdx = match.index;
        const endIdx = startIdx + match[0].length;
        if ((startIdx > 0 && text[startIdx - 1] === '{') || (endIdx < text.length && text[endIdx] === '}')) {
            continue;
        }

        const maleVariant = match[1].trim();
        const femaleVariant = match[2].trim();
        const neutralVariant = match[3] ? match[3].trim() : null;
        const explicitVariable = match[4] ? match[4].trim() : null;

        const inferredRaw = explicitVariable || findNearestVariable(text, match.index);
        const inferredVariable = inferredRaw ? String(inferredRaw).split('|')[0].trim() : null;

        agreements.push({
            fullMatch: match[0],
            maleVariant,
            femaleVariant,
            neutralVariant,
            explicitVariable,
            position: match.index,
            // Если переменная не указана явно, ищем ближайшую слева
            inferredVariable
        });
    }

    return agreements;
}

/**
 * Выбирает правильный вариант на основе рода
 * @param {string} maleVariant - Вариант для мужского рода
 * @param {string} femaleVariant - Вариант для женского рода
 * @param {'male'|'female'|'neuter'|'unknown'} gender - Определённый род
 * @returns {string} - Выбранный вариант
 */
function selectVariant(maleVariant, femaleVariant, neutralVariant, gender) {
    if (gender === 'female') {
        return femaleVariant;
    }
    if (gender === 'neuter' && neutralVariant) {
        return neutralVariant;
    }
    return maleVariant;
}

/**
 * Обрабатывает текст и заменяет конструкции согласования на правильные варианты
 * @param {string} text - Исходный текст с конструкциями согласования
 * @param {object} variablesData - Объект с данными всех переменных
 * @returns {string} - Обработанный текст
 */
export function resolveGenderAgreement(text, variablesData = {}) {
    if (!text || typeof text !== 'string') {
        return text;
    }

    // Парсим все конструкции согласования
    const agreements = parseGenderAgreement(text);

    if (agreements.length === 0) {
        return text;
    }

    // Обрабатываем конструкции в обратном порядке (от конца к началу)
    // чтобы не нарушать позиции при замене
    let result = text;

    for (let i = agreements.length - 1; i >= 0; i--) {
        const agreement = agreements[i];
        const { fullMatch, maleVariant, femaleVariant, neutralVariant, inferredVariable, explicitVariable } = agreement;

        // Определяем род по переменной
        let gender = 'unknown';
        const candidates = [];

        // Явно указанные переменные можно перечислять через двоеточие {..:var1:var2}
        if (explicitVariable) {
            explicitVariable.split(':').map(v => v.trim()).filter(Boolean).forEach(v => candidates.push(v));
        }
        if (inferredVariable) {
            candidates.push(inferredVariable);
        }

        // Ищем подходящий род: если встретили neuter — используем его приоритетно.
        let firstKnown = null;
        for (const candidate of candidates) {
            const g = detectGenderFromVariable(candidate, variablesData);
            if (g && g !== 'unknown') {
                if (!firstKnown) firstKnown = g;
                if (g === 'neuter') {
                    gender = 'neuter';
                    break;
                }
                gender = g;
            }
        }
        if (gender === 'unknown' && firstKnown) {
            gender = firstKnown;
        }

        // Выбираем правильный вариант
        const selectedVariant = selectVariant(maleVariant, femaleVariant, neutralVariant, gender);

        // Заменяем конструкцию на выбранный вариант
        result = result.substring(0, agreement.position) +
            selectedVariant +
            result.substring(agreement.position + fullMatch.length);
    }

    return result;
}

/**
 * Проверяет, содержит ли текст конструкции согласования
 * @param {string} text - Текст для проверки
 * @returns {boolean} - true если содержит
 */
export function hasGenderAgreement(text) {
    if (!text || typeof text !== 'string') {
        return false;
    }

    return GENDER_AGREEMENT_REGEX.test(text);
}

/**
 * Валидирует синтаксис конструкции согласования
 * @param {string} text - Текст для валидации
 * @returns {Array} - Массив ошибок (пустой если ошибок нет)
 */
export function validateGenderAgreement(text) {
    if (!text || typeof text !== 'string') {
        return [];
    }

    const errors = [];
    const agreements = parseGenderAgreement(text);

    agreements.forEach((agreement, index) => {
        // Проверяем, что оба варианта не пустые
        if (!agreement.maleVariant) {
            errors.push({
                position: agreement.position,
                message: 'Мужской вариант не может быть пустым',
                agreement: agreement.fullMatch
            });
        }

        if (!agreement.femaleVariant) {
            errors.push({
                position: agreement.position,
                message: 'Женский вариант не может быть пустым',
                agreement: agreement.fullMatch
            });
        }

        // Проверяем, что найдена переменная для определения рода
        if (!agreement.inferredVariable) {
            errors.push({
                position: agreement.position,
                message: 'Не найдена переменная для определения рода. Укажите переменную явно: {вариант1|вариант2:variable_name}',
                agreement: agreement.fullMatch
            });
        }
    });

    return errors;
}

export default {
    parseGenderAgreement,
    resolveGenderAgreement,
    hasGenderAgreement,
    validateGenderAgreement
};
